import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileStack, Upload, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocRow } from "@/components/realestate/DocRow";

// Items where a reusable template makes sense
export const TEMPLATE_TARGETS = [
  ["general", "General (sin asociar)"], ["contrato", "Contrato de gestión"], ["autorizacion_cee_1", "Autorización CEE"],
  ["datos_vendedor", "Datos del vendedor"], ["ficha_tecnica", "Ficha técnica"], ["partes_visita", "Hoja de visita"],
  ["ofertas", "Oferta"], ["datos_comprador", "Datos del comprador"], ["plantilla_arras", "Plantilla verde Arras"], ["formas_pago", "Formas de pago"],
];
const TARGET_LABEL = Object.fromEntries(TEMPLATE_TARGETS);

export const DocTemplatesPanel = ({ workspaceId, canWrite }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("general");
  const [busy, setBusy] = useState(false);
  const { data: templates = [] } = useQuery({
    queryKey: ["realestate-doc-templates", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/realestate/templates`)).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["realestate-doc-templates", workspaceId] });

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("entity_type", "realestate_template");
        form.append("entity_id", workspaceId);
        form.append("tags", target === "general" ? "template" : `template,${target}`);
        await api.post(`/workspaces/${workspaceId}/files`, form);
      }
      toast.success("Plantilla guardada");
      refresh();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="doc-templates-panel">
      <button className="flex items-center gap-2 w-full text-left" onClick={() => setOpen(!open)} data-testid="doc-templates-toggle">
        <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><FileStack size={16} /></span>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Plantillas de documentos</h3>
          <p className="text-xs text-muted-foreground">Arras verde, datos vendedor/comprador, hoja de visita… reutilizables en cada inmueble.</p>
        </div>
        <Badge variant="secondary" className="text-[10px]">{templates.length}</Badge>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="space-y-2 pt-1">
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="doc-template-target"><SelectValue /></SelectTrigger>
                <SelectContent>{TEMPLATE_TARGETS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <label className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline cursor-pointer" data-testid="doc-template-upload">
                <Upload size={13} /> {busy ? "Subiendo…" : "Subir plantilla"}
                <input type="file" multiple className="hidden" onChange={upload} disabled={busy} />
              </label>
            </div>
          )}
          {templates.length === 0 && <p className="text-xs text-muted-foreground">Aún no hay plantillas. Sube aquí los documentos base que usas en cada operación.</p>}
          <div className="grid md:grid-cols-2 gap-1.5">
            {templates.map((t) => {
              const tag = (t.tags || []).find((x) => x !== "template");
              return (
                <div key={t.id} className="space-y-0.5">
                  <DocRow file={t} onChanged={refresh} canWrite={canWrite} />
                  {tag && <p className="text-[10px] text-muted-foreground pl-1">↳ {TARGET_LABEL[tag] || tag}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};
